import type { LeagueGameKey } from "./types";

/**
 * Default chips per ruby (T20 / The Hundred / duration leagues / unknown format).
 * Seat chips by format: T10 → {@link CHIPS_PER_RUBY_T10}, T20 → this default,
 * F50 → {@link CHIPS_PER_RUBY_T50}.
 */
export const CHIPS_PER_RUBY = 2000;
/** Seat chips for T10 match leagues. */
export const CHIPS_PER_RUBY_T10 = 1000;
/** Seat chips for 50-over (F50 / ODI) match leagues. */
export const CHIPS_PER_RUBY_T50 = 3000;

/** Entry rubies convert to this many coins in the prize pot (per ruby paid in). */
export const PRIZE_COINS_PER_RUBY = 1000;

/** One seat top-up pack: rubies spent → chips + pot growth at the rates above. */
export const SEAT_TOPUP_RUBIES = 1;
/** Max seat top-up packs a member may buy in one league (T20 / T10 / Hundred / duration). */
export const SEAT_TOPUP_MAX = 3;
/** Max seat top-up packs in a 50-over (T50 / ODI) match league — one more than the default. */
export const SEAT_TOPUP_MAX_T50 = 4;
/** Seat top-up is allowed while counting chips are at this balance or lower. */
export const SEAT_TOPUP_AVAILABLE_AT_OR_BELOW = 100;

/** Chips credited per ruby for a match format (overs code: 10 / 20 / 50 / 100). */
export function chipsPerRubyForFormat(formatOvers: number | null | undefined): number {
    const n = Number(formatOvers);
    if (Number.isFinite(n) && n === 10) return CHIPS_PER_RUBY_T10;
    if (Number.isFinite(n) && n === 50) return CHIPS_PER_RUBY_T50;
    return CHIPS_PER_RUBY;
}

/** Max seat top-up packs for a match format (overs code: 10 / 20 / 50 / 100). */
export function seatTopUpMax(formatOvers: number | null | undefined): number {
    const n = Number(formatOvers);
    if (Number.isFinite(n) && n === 50) return SEAT_TOPUP_MAX_T50;
    return SEAT_TOPUP_MAX;
}

export const LEAGUE_ENTRY_FEE_PRESETS = [1, 2, 5, 10, 25] as const;
export const LEAGUE_ENTRY_FEE_MIN = 1;
export const LEAGUE_ENTRY_FEE_MAX = 100;

/** Real joiners besides the host required by kickoff (1 = host + one friend). Under-filled leagues are cancelled and rubies refunded. */
export const LEAGUE_MIN_REAL_JOINERS = 1;

/** Default duration when the host picks "duration" — spec says default 1 day, minimum >1 day out. */
export const LEAGUE_DEFAULT_DURATION_DAYS = 1;
export const LEAGUE_DURATION_PRESETS: { label: string; days: number }[] = [
    { label: "1 day", days: 1 },
    { label: "3 days", days: 3 },
    { label: "1 week", days: 7 },
    { label: "2 weeks", days: 14 },
];

export const LEAGUE_NAME_MAX_LENGTH = 40;

/** Prototype viewer id for `/leaguesdemo`. Live leagues use the logged-in account id. */
export const LEAGUE_VIEWER_USER_ID = 1;

/** Play tabs, leaderboard game tabs (after Overall), create picker, activity filters. */
export const LEAGUE_GAME_KEYS: LeagueGameKey[] = ["squares", "sixes", "bullion", "trader"];

/** Create-league match picker only lists fixtures kicking off within this window. */
export const LEAGUE_CREATE_MATCH_WINDOW_HOURS = 72;
export const LEAGUE_CREATE_MATCH_WINDOW_MS = LEAGUE_CREATE_MATCH_WINDOW_HOURS * 60 * 60 * 1000;

/** Spotlight “Starting soon” bucket — fixtures kicking off within this window. */
export const LEAGUE_CREATE_SOON_MS = 3 * 60 * 60 * 1000;

/** Warn hosts to invite friends when the selected match starts within this window. */
export const LEAGUE_CREATE_NEAR_START_WARN_MS = LEAGUE_CREATE_SOON_MS;

export const LEAGUE_GAME_META: Record<LeagueGameKey, { label: string; icon: string; path: string }> = {
    trader: { label: "Trader", icon: "/assets/trader.png", path: "/trader" },
    bullion: { label: "Bullion+", icon: "/assets/bullion.png", path: "/bullion" },
    sixes: { label: "Sixes", icon: "/assets/sixes.png", path: "/sixes" },
    squares: { label: "Squares+", icon: "/assets/squares.png", path: "/squares" },
};

/**
 * Accent fills for Overall leaderboard progress bars (won = game colour / gradient).
 * Legend swatches use the same values via `leagueGameBarFillStyle`.
 */
export const LEAGUE_GAME_BAR_COLORS: Record<LeagueGameKey, string> = {
    trader: "#F9D849",
    squares: "#AF2EF6",
    sixes: "#32F78D",
    bullion:
        "linear-gradient(107.25deg, #F9FBC7 17.65%, #E6C878 24.64%, #FBF0A8 32.44%, #7B5E2C 72.7%, #AE8E53 82.15%, #AE8E53 89.54%, #7C582D 103.1%)",
};

/** Solid colour for Bullion when a flat swatch is required (e.g. tiny chips). */
export const LEAGUE_GAME_BAR_BULLION_SOLID = "#AE8E53";

export function leagueGameBarFillStyle(fill: string): { background?: string; backgroundColor?: string } {
    if (fill.startsWith("linear-gradient")) return { background: fill };
    return { backgroundColor: fill };
}

/** @deprecated Pot segments removed from leaderboard bars; kept for member modal chrome. */
export const LEAGUE_LB_POT_BAR_COLOR = "rgba(255, 255, 255, 0.28)";

/** Leaderboard bar track — full width per row; game colours fill the won share. */
export const LEAGUE_LB_PRED_BAR_BG = "rgba(27, 6, 47, 0.3)";
/** Leaderboard member row backgrounds. */
export const LEAGUE_LB_ROW_BG_CLASS = "bg-[#1B062F]/30";
export const LEAGUE_LB_ROW_BG_VIEWER_CLASS = "bg-[#FC51BB]/30";
/** Solid identical wash for `?preview=1` TV boards (no tint from the card behind). */
export const LEAGUE_LB_ROW_BG_TV_CLASS = "bg-[#4E2188]";
export const LEAGUE_LB_PRED_BAR_HEIGHT_CLASS = "h-[10px]";
/** Leaderboard rank badge (20×20). */
export const LEAGUE_LB_RANK_BADGE_CLASS =
    "ml-1.5 flex h-[20px] w-[20px] shrink-0 items-center justify-center bg-white font-anton text-[10px] font-normal leading-none text-main sm:ml-2";
/** Leaderboard player name in the row. */
export const LEAGUE_LB_USERNAME_CLASS =
    "align-middle font-anton text-[12px] font-normal uppercase leading-[22px] tracking-[0] text-white [leading-trim:none]";
/** `?preview=1` TV — same size as activity game names (`LEAGUE_ACTIVITY_BODY_LINE_CLASS`). */
export const LEAGUE_LB_USERNAME_TV_CLASS =
    "align-middle font-anton text-[16px] font-normal uppercase leading-[22px] tracking-[0] text-white [leading-trim:none]";
/** Leaderboard table grid (rank · move · avatar · name/bar · pts · chevron). */
export const LEAGUE_LB_TABLE_GRID_CLASS =
    "grid-cols-[20px_12px_30px_minmax(0,1fr)_3.25rem_2rem]";
/** Stat cells — fill their grid track and center header/value. */
export const LEAGUE_LB_STAT_PTS_COL_CLASS = "flex w-full min-w-0 items-center justify-center";
/** Open Bullion projected pool-share column. */
export const LEAGUE_LB_STAT_IFNOW_COL_CLASS = "flex w-full items-center justify-center";
export const LEAGUE_LB_BULLION_IF_NOW_HINT =
    "If it ended now — pick score plus place bonus from current rank.";
/** Same tracks as {@link LEAGUE_LB_TABLE_GRID_CLASS} with a wider PTS + if-now pair. */
export const LEAGUE_LB_TABLE_GRID_BULLION_IFNOW_CLASS =
    "grid-cols-[20px_12px_30px_minmax(0,1fr)_3.25rem_2.75rem_2rem]";
/** Chevron track — same 32px as the stats button so header labels line up with values. */
export const LEAGUE_LB_CHEVRON_COL_CLASS = "h-8 w-8 justify-self-end";
/** Leaderboard muted numeric column (Bullion IF NOW). */
export const LEAGUE_LB_CHIPS_POINTS_CLASS =
    "text-center font-anton text-[12px] font-normal uppercase leading-none tracking-[0] tabular-nums text-white/50 [leading-trim:none]";
/** @deprecated Use LEAGUE_LB_CHIPS_POINTS_CLASS */
export const LEAGUE_LB_POT_POINTS_CLASS = LEAGUE_LB_CHIPS_POINTS_CLASS;
export const LEAGUE_LB_WON_POINTS_CLASS =
    "whitespace-nowrap text-center font-anton text-[12px] font-normal uppercase leading-none tracking-[0] tabular-nums text-white [leading-trim:none]";
export const LEAGUE_LB_PTS_HEADER_CLASS =
    "text-center font-anton text-[12px] font-normal uppercase leading-none tracking-[0] text-white/50 [leading-trim:none]";

export const RUBY_ICON_SRC = "/img/rubin_sm.png";
export const COIN_ICON_SRC = "/assets/currency-gold-ball.png";
/** League seat bankroll — Balls (not the prize pot). */
export const CHIP_ICON_SRC = "/assets/red-ball.png";
/** Gold league trophy mark (filename cache-busts the old shield crest). */
export const LEAGUE_ICON_SRC = "/assets/league-trophy.png";
export const LEAGUE_MODE_ICON_SRC = LEAGUE_ICON_SRC;
/** My Games / My Zone tray thumb — private league (trophy + lock badge). */
export const LEAGUE_MYZONE_PRIVATE_ICON_SRC = "/assets/league-myzone-private.png?v=5";
/** My Games / My Zone tray thumb — public league (trophy + globe badge). */
export const LEAGUE_MYZONE_PUBLIC_ICON_SRC = "/assets/league-myzone-public.png?v=5";

/** Header / tray mark for a league’s public vs private listing. */
export function leagueListingIconSrc(listing?: "private" | "public" | null): string {
    return listing === "public" ? LEAGUE_MYZONE_PUBLIC_ICON_SRC : LEAGUE_MYZONE_PRIVATE_ICON_SRC;
}

/** Visible back label on in-league game play pages (Bullion board, Sixes card, league header bar). */
export const LEAGUE_GAME_PLAY_BACK_LABEL = "LEAGUE";
/** Accessible label for the same back control (icon-only chrome headers). */
export const LEAGUE_GAME_PLAY_BACK_ARIA_LABEL = "Back to league";
/** League mode header when viewing the league detail / lobbies (leaves league mode). */
export const LEAGUE_EXIT_BACK_LABEL = "EXIT";
export const LEAGUE_EXIT_BACK_ARIA_LABEL = "Exit league";

/** How often the prototype advances live leagues (settles due plays, places bot plays). */
export const LEAGUE_SIMULATION_TICK_MS = 2000;

/** How long a play the viewer places sits in-play before it settles. */
export const LEAGUE_PLAY_SETTLE_DELAY_MS = 9000;
/** Bot plays settle somewhere in this window so the feed keeps moving. */
/** Bot plays settle somewhere in this window so the feed keeps moving. */
export const LEAGUE_BOT_PLAY_SETTLE_MIN_MS = 4000;
export const LEAGUE_BOT_PLAY_SETTLE_MAX_MS = 16000;
/** Chance per tick that a bot places a new play in a live league. */
export const LEAGUE_BOT_PLAY_CHANCE = 0.62;
/** Bots stop adding plays once a league already has this many open. */
export const LEAGUE_MAX_OPEN_BOT_PLAYS = 6;

/** How long a rank change keeps showing its up/down arrow. */
export const LEAGUE_MOVEMENT_TTL_MS = 90000;

/** Desktop sticky standings: show the full table until the roster exceeds this — then cap height and scroll. */
export const LEAGUE_STANDINGS_DESKTOP_SCROLL_AT = 20;

export const LEAGUE_PLAY_STAKE_PRESETS = [100, 250, 500, 1000] as const;
export const LEAGUE_PLAY_STAKE_MIN = 50;

/**
 * League coins held when a member opens their league SIXES card. Free by default (0) — the card is
 * issued on join/create. Mirrors `SIXES_CARD_ENTRY_STAKE` in the backend league config.
 */
export const LEAGUE_SIXES_CARD_STAKE = 0;

/**
 * League-coin entry stake for the shared Bullion battle. Free by default (0) — members pick until
 * the configured play lock (see LEAGUE_BULLION_ENTRY_LOCK_*) or get an auto lucky-dip slip after.
 * Mirrors `BULLION_BATTLE_ENTRY_STAKE` in the backend.
 */
export const LEAGUE_BULLION_BATTLE_STAKE = 0;

/**
 * Chips added per cell tap on Super Squares (and minimum play stake — one tap already qualifies).
 * Each further tap on the same cell stacks another +10. Same step as public Squares.
 */
export const LEAGUE_SQUARES_MIN_STAKE = 10;

/** Max league chips that can be staked on a single Super Squares over. */
export const LEAGUE_SQUARES_MAX_STAKE = 200;

/**
 * Smallest league-chip Trader stake.
 * Must match `TRADER_MIN_STAKE` in the backend league config and league-mode trade slips.
 */
export const LEAGUE_TRADER_MIN_STAKE = 10;

/** Chips a member needs to place one play in each game (0 = free entry). */
export const LEAGUE_GAME_MIN_STAKE: Record<LeagueGameKey, number> = {
    trader: LEAGUE_TRADER_MIN_STAKE,
    squares: LEAGUE_SQUARES_MIN_STAKE,
    sixes: LEAGUE_SIXES_CARD_STAKE,
    bullion: LEAGUE_BULLION_BATTLE_STAKE,
};

/**
 * XP at league settle (mirrors backend finalize):
 *   - floor(leaguePoints × LEAGUE_XP_POINTS_PCT / 100), capped at LEAGUE_XP_POINTS_CAP
 *   - LEAGUE_XP_GAME_WIN for topping each included game type
 *   - LEAGUE_XP_PLACEMENT_BONUS for overall 1st / 2nd / 3rd
 * Only players with ≥1 settled counting play; XP is live once the league has started.
 */
/** Percent of league points converted to XP (before the hard cap). */
export const LEAGUE_XP_POINTS_PCT = 5;
/** Max XP from the points conversion in a single league (anti-farm). */
export const LEAGUE_XP_POINTS_CAP = 500;
/** Fixed XP for topping an included game type (ties share). */
export const LEAGUE_XP_GAME_WIN = 50;
export const LEAGUE_XP_PLACEMENT_BONUS = [150, 90, 50];

/**
 * Coin prize pot share by finishing place.
 * 1 player: 100%. 2 players: 70/30. 3+: top 3 at 50/30/20.
 */
export const LEAGUE_PRIZE_SPLITS: Record<number, readonly number[]> = {
    1: [1],
    2: [0.7, 0.3],
};
export const LEAGUE_PRIZE_SPLIT_DEFAULT = [0.5, 0.3, 0.2] as const;

export const LEAGUE_CARD_GRADIENT = "bg-[linear-gradient(to_right,#623998,#652DAF)]";
export const LEAGUE_CARD_GRADIENT_MUTED =
    "bg-[linear-gradient(to_right,rgba(98,57,152,0.5),rgba(101,45,175,0.5))]";
export const LEAGUE_HEADING_CLASS =
    "font-anton font-normal not-italic uppercase leading-snug tracking-[0%] text-white [leading-trim:none]";
/** Squares+ / Sixes / Bullion+ / Trader play tabs — also used by Leaderboard / Activity section tabs. */
export const LEAGUE_GAME_TAB_LABEL_CLASS =
    "align-middle font-anton text-[clamp(11px,4cqi,16px)] font-normal uppercase not-italic leading-none tracking-[0%] text-white [leading-trim:none]";
export const LEAGUE_BODY_CLASS =
    "font-chakra font-normal not-italic normal-case tracking-[0%] [leading-trim:none]";
export const LEAGUE_PRIMARY_BUTTON_CLASS =
    "inline-flex items-center justify-center gap-2 border-0 bg-lime px-4 font-anton text-[14px] font-normal uppercase leading-none text-main transition-colors hover:bg-limehover disabled:cursor-not-allowed disabled:opacity-50";
export const LEAGUE_SECONDARY_BUTTON_CLASS =
    "inline-flex items-center justify-center gap-2 border border-solid border-white/35 bg-transparent px-4 font-anton text-[14px] font-normal uppercase leading-none text-white transition-colors hover:border-white/60 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50";
/**
 * Wallet gap row — small breathing room by default; full `py-3` when the bow–bat strip
 * is visible (`data-delivery-active` on {@link SquaresMatchBarDelivery}).
 * Padding eases with the strip's height animation so show/hide doesn't snap.
 */
export const LEAGUE_DETAIL_WALLET_GAP_ROW_CLASS =
    "py-1.5 transition-[padding] duration-[750ms] ease-[cubic-bezier(0.4,0,0.2,1)] [&:has([data-delivery-active])]:py-3";
/** “LEAGUE WALLET” label in the league mode site header. */
export const LEAGUE_WALLET_LABEL_CLASS =
    "text-right font-anton text-[10px] font-normal uppercase leading-[100%] tracking-[0] text-white [leading-trim:none] lg:text-[16px]";
/** League wallet chip / ruby icon — 24px mobile, 28px desktop. */
export const LEAGUE_WALLET_CHIP_ICON_CLASS =
    "h-6 w-6 shrink-0 object-contain drop-shadow-[0_3px_4px_rgba(0,0,0,0.325)] lg:h-7 lg:w-7";
/** Chip / ruby balance in the centred league wallet. */
export const LEAGUE_WALLET_BALANCE_CLASS =
    "font-anton text-[16px] font-normal uppercase leading-[100%] tracking-[0] tabular-nums text-white [leading-trim:none] phone-sm:text-[17px] sm:text-[18px] lg:text-[22px]";
/** Pink 20×20 square “+” on the league chips wallet (whole pill opens top-up). */
export const LEAGUE_WALLET_TOPUP_PLUS_CLASS =
    "inline-flex h-5 w-5 shrink-0 items-center justify-center border-0 bg-[#F47AA8] p-0 transition-colors hover:bg-[#F890B6]";
/** League mode site header — EXIT / back control. */
export const LEAGUE_HEADER_EXIT_ICON_CLASS =
    "h-5 w-5 shrink-0 text-white sm:h-6 sm:w-6 lg:h-[30px] lg:w-[30px]";
export const LEAGUE_HEADER_EXIT_LABEL_CLASS =
    "font-anton text-[13px] font-normal uppercase leading-[100%] tracking-[0.04em] text-center align-middle text-white [leading-trim:none] phone-sm:text-[14px] sm:text-[16px] lg:text-[24px] lg:tracking-[0]";
/** League mode site header — wallet chip container. */
export const LEAGUE_HEADER_WALLET_CHROME_CLASS =
    "box-border inline-flex h-[34px] shrink-0 items-center justify-center gap-1.5 border-0 bg-white/20 px-2 py-0 font-anton uppercase text-white shadow-none sm:gap-2 sm:px-2.5 lg:h-auto lg:min-h-[40px] lg:gap-2.5 lg:px-4 lg:py-2";
/** Compact filled lime CTA for league lobby section headers. */
export const LEAGUE_LOBBY_HEADER_CTA_CLASS =
    "inline-flex h-[26px] shrink-0 items-center border-0 bg-lime px-2.5 font-anton text-[11px] uppercase leading-none text-main transition-colors hover:bg-limehover disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/40";
/** Ghost outline variant when a secondary header action is needed. */
export const LEAGUE_LOBBY_HEADER_CTA_GHOST_CLASS =
    "inline-flex h-[26px] shrink-0 items-center border border-solid border-lime bg-transparent px-2 font-anton text-[11px] uppercase leading-none text-lime transition-colors hover:bg-lime/15 disabled:cursor-not-allowed disabled:border-white/20 disabled:bg-transparent disabled:text-white/40";
/** Full-width primary CTA under lobby previews (Sixes Open/Reveal, etc.). */
export const LEAGUE_LOBBY_FULL_WIDTH_CTA_CLASS =
    "flex h-[40px] w-full items-center justify-center border-0 bg-lime text-center font-anton text-[16px] font-normal uppercase leading-none tracking-normal text-main transition-colors hover:bg-limehover disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/60";
/** Compact footer CTA on contest-style lobby rows (Bullion Enter / View). */
export const LEAGUE_LOBBY_FOOTER_CTA_CLASS =
    "inline-flex h-[30px] shrink-0 items-center justify-center border-0 bg-lime px-2.5 font-anton text-[11px] uppercase leading-none text-main transition-colors hover:bg-limehover disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/60 lg:px-3 lg:text-[12px]";
export const LEAGUE_LOBBY_FOOTER_CTA_MUTED_CLASS =
    "inline-flex h-[30px] shrink-0 items-center justify-center border-0 bg-white px-2.5 font-anton text-[11px] uppercase leading-none text-main transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/60 lg:px-3 lg:text-[12px]";
/** Lime status / over chip in lobby toolbars. */
export const LEAGUE_LOBBY_STATUS_PILL_CLASS =
    "shrink-0 bg-lime px-1.5 py-0.5 font-anton text-[10px] uppercase leading-none text-main";
/** Inner play surface under a lobby section header — same mid-purple as Sixes lobby cards. */
export const LEAGUE_LOBBY_CARD_SURFACE_CLASS =
    "min-w-0 overflow-hidden bg-[linear-gradient(to_right,#4e2384,#4d287a)]";
/** Body under lobby card toolbars — brighter Squares purple so the grid panel isn’t near-black. */
export const LEAGUE_LOBBY_CARD_BODY_CLASS = "bg-[#5B2A9A]";
/** Nested block inside a lobby card body (finished over rows, etc.). */
export const LEAGUE_LOBBY_CARD_NESTED_CLASS =
    "bg-[#6B35B0] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] hover:bg-[#7640BC]";
/** Toolbar strip inside a lobby card (tabs, entry, secondary CTA). */
export const LEAGUE_LOBBY_CARD_TOOLBAR_CLASS =
    "flex items-center gap-2 border-0 border-b border-solid border-white/10 bg-[#2E135B]/45 px-2 py-1.5";

/** Leagues header band — site header, scorecard panel, lobby banner, modal chrome. */
export const LEAGUE_HEADER_RADIAL_GRADIENT =
    "radial-gradient(50% 50% at 50% 50%, #652DAF 0%, #7C26EC 100%)";

/**
 * League detail — shared horizontal gutter inside `c8-shell` (site header, meta, scorecard, body).
 * Keeps EXIT / league name / white scorecard / three-column cards on one vertical edge grid (desktop).
 */
export const LEAGUE_DETAIL_GUTTER_X_CLASS =
    "px-2 phone-sm:px-2.5 md:px-3 lg:px-5 2xl:px-0";

/** Site header row — EXIT LEAGUE + league wallet. */
export const LEAGUE_MODE_HEADER_SHELL_ROW_CLASS = `c8-shell ${LEAGUE_DETAIL_GUTTER_X_CLASS}`;

/** Horizontal inset from `lg` — matches `LEAGUE_DETAIL_GUTTER_X_CLASS` at desktop breakpoints. */
export const LEAGUE_DETAIL_GUTTER_LG_CLASS = "lg:px-5 2xl:px-0";

/** League detail blocks that should span the viewport below `lg` (scorecard, play, leaderboard). */
export const LEAGUE_DETAIL_MOBILE_BLEED_SHELL_CLASS =
    `w-full max-w-none px-0 max-lg:c8-bleed lg:c8-shell ${LEAGUE_DETAIL_GUTTER_LG_CLASS}`;

/** White scorecard + detail tabs. */
export const LEAGUE_DETAIL_SHELL_INSET_CLASS = LEAGUE_DETAIL_MOBILE_BLEED_SHELL_CLASS;

/** Main column under scorecard (wallet gap + My Games / Leaderboard / Activity). */
export const LEAGUE_DETAIL_PAGE_SHELL_CLASS =
    `flex min-h-[600px] flex-col pb-6 ${LEAGUE_DETAIL_MOBILE_BLEED_SHELL_CLASS}`;

/** League Mode accents — brand keeps purple + lime CTAs; gold is league-only chrome. */
export const LEAGUE_MODE_GOLD = "#FFC72C";
export const LEAGUE_MODE_GOLD_DEEP = "#D4AF37";
export const LEAGUE_MODE_CARD_BG = "#2E135B";
export const LEAGUE_MODE_CARD_BG_SOFT = "#3A196E";
/** Desktop league detail — My Games / Leaderboard / Activity column cards. */
export const LEAGUE_DESKTOP_COLUMN_BG = "#652DAF";
export const LEAGUE_DESKTOP_COLUMN_TITLE_CLASS =
    "align-middle font-anton text-[20px] font-normal not-italic uppercase leading-[100%] tracking-[0] !text-white [leading-trim:none]";
/** Inner padding inside each desktop column card (MY GAMES / LEADERBOARD / ACTIVITY). */
export const LEAGUE_DESKTOP_COLUMN_INSET_X_CLASS = "px-3 lg:px-4";
/** Space between column title row and body (My Games / Leaderboard / Activity). */
export const LEAGUE_DESKTOP_COLUMN_TITLE_GAP_CLASS = "mb-2 lg:mb-3";
/** Title band height — matches My Games avatar row. */
export const LEAGUE_DESKTOP_COLUMN_TITLE_ROW_CLASS =
    `flex min-h-[30px] min-w-0 items-center gap-2 ${LEAGUE_DESKTOP_COLUMN_TITLE_GAP_CLASS}`;

/** How long each Overall / game / Activity filter stays before the TV / auto-cycle advances. */
export const LEAGUE_BOARD_TAB_CYCLE_MS = 14_000;
/** After a manual tab click, wait this long before cycling again. */
export const LEAGUE_BOARD_TAB_CYCLE_RESUME_MS = 40_000;

/** Gold outline for League Mode header / standings chrome (no radius). */
export const LEAGUE_MODE_GOLD_BORDER_CLASS = "border-2 border-solid border-[#FFC72C]";
export const LEAGUE_MODE_GOLD_BORDER_THIN_CLASS = "border border-solid border-[#FFC72C]";

/** Activity / open-play row Summary CTA — matches Friends / notifications pill. */
export const LEAGUE_ACTIVITY_CTA_PILL_CLASS =
    "inline-flex h-[22px] min-w-[4rem] w-[4rem] max-h-[22px] shrink-0 items-center justify-center whitespace-nowrap rounded-none border-0 bg-white px-1.5 py-0 text-center font-anton text-[9px] font-normal uppercase leading-none tracking-wide text-black transition-opacity hover:opacity-90 sm:min-w-[5.25rem] sm:w-[5.25rem] sm:px-2 sm:text-[10px]";

/** Activity feed / open-play row — fixed 60px strip. */
export const LEAGUE_ACTIVITY_ROW_CLASS =
    "relative flex h-[60px] min-h-[60px] max-h-[60px] w-full min-w-0 items-center gap-2.5 overflow-hidden bg-[#1B062F]/30 px-2.5";

/** Player name + rank line on activity rows. */
export const LEAGUE_ACTIVITY_NAME_LINE_CLASS =
    "m-0 flex min-w-0 items-center gap-1.5 overflow-hidden font-anton text-[14px] font-normal not-italic uppercase leading-[22px] tracking-[0%] [leading-trim:none]";

/** Activity verb / points / game line. */
export const LEAGUE_ACTIVITY_BODY_LINE_CLASS =
    "m-0 flex min-w-0 items-center gap-1 overflow-hidden font-anton text-[16px] font-normal not-italic uppercase leading-[22px] tracking-[0%] text-white [leading-trim:none]";

/** Relative time on the right of an activity row. */
export const LEAGUE_ACTIVITY_TIME_CLASS =
    "shrink-0 self-start pt-0.5 font-chakra text-[11px] font-normal leading-[16px] text-white/45";

/** Short game names for activity “ON {game}.” copy. */
export const LEAGUE_ACTIVITY_GAME_LABEL: Record<LeagueGameKey, string> = {
    trader: "TRADER",
    squares: "SQUARES+",
    bullion: "BULLION+",
    sixes: "SIXES",
};

/** Initial window + step for Activity / open-play list pagination (client-side). */
export const LEAGUE_ACTIVITY_PAGE_SIZE = 20;
